/**
 * Legal Page Layout
 * Shared layout component for Terms, Privacy, Cookies, and DPA pages
 * Accessible without authentication, mobile responsive, dark mode compatible
 */

import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { LEGAL_URLS, CANONICAL_DOMAIN } from '@/constants/legalVersions';

export default function LegalPageLayout({ 
  title, 
  lastUpdated, 
  children,
  metaDescription = ''
}) {
  // Set document title and meta
  useEffect(() => {
    document.title = `${title} | FinList`;
    
    // Update meta description
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.content = metaDescription || `${title} for FinList - Your smart personal finance companion`;
    
    // Add noindex for legal pages (optional, prevents SEO indexing)
    let robotsTag = document.querySelector('meta[name="robots"]');
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.name = 'robots';
      document.head.appendChild(robotsTag);
    }
    robotsTag.content = 'noindex, nofollow';
    
    return () => {
      // Cleanup
      if (robotsTag) robotsTag.content = 'index, follow';
    };
  }, [title, metaDescription]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
            >
              <img 
                src="/images/welcome/finlist-logo.png" 
                alt="FinList" 
                className="w-8 h-8 rounded-lg"
              />
              FinList
            </Link>
            <nav className="flex items-center gap-4 text-sm">
              <Link 
                to="/signin" 
                className="text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              >
                Sign In
              </Link>
              <Link 
                to="/signup" 
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
              >
                Get Started
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            {title}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Legal Content */}
        <article className="prose prose-gray dark:prose-invert max-w-none
          prose-headings:font-semibold
          prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:border-b prose-h2:border-gray-200 prose-h2:dark:border-slate-700 prose-h2:pb-2
          prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
          prose-p:leading-relaxed prose-p:text-gray-700 prose-p:dark:text-gray-300
          prose-li:text-gray-700 prose-li:dark:text-gray-300
          prose-a:text-brand-600 prose-a:dark:text-brand-400 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-gray-900 prose-strong:dark:text-white
          prose-ul:space-y-1
        ">
          {children}
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-gray-500 dark:text-gray-400 text-sm">
              © {new Date().getFullYear()} FinList. All rights reserved.
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <Link 
                to={LEGAL_URLS.terms}
                className="text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              >
                Terms of Service
              </Link>
              <Link 
                to={LEGAL_URLS.privacy}
                className="text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              >
                Privacy Policy
              </Link>
              <Link 
                to={LEGAL_URLS.cookies}
                className="text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              >
                Cookies Policy
              </Link>
              <Link 
                to={LEGAL_URLS.dpa}
                className="text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              >
                DPA
              </Link>
            </nav>
          </div>
          <div className="mt-4 text-center text-xs text-gray-400 dark:text-gray-500">
            <a 
              href={CANONICAL_DOMAIN}
              className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              {CANONICAL_DOMAIN}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
